package org.example.project.enginepersonal

import org.example.project.settings.UISettings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.delay
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.screens_cus.UrlaubsApi
import org.example.project.screens_cus.Urlaubsanspruch
import org.example.project.service.GlobalManager
import org.example.project.enginekalender.EngineMonthCalendar
import org.example.project.enginekalender.kalenderTodayIsoDate
import org.example.project.enginetabular.TabularScreen
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaCheckbox
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.example.project.utils.UserAvatarCircle
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Promise

private val personalAkteSnapshotJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

@Composable
fun PersonalverwaltungScreen(art: String) {
    val scope = rememberCoroutineScope()
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("personal"),
        GlobalManager.jsonDomainVersion("user"),
        GlobalManager.jsonDomainVersion("urlaub"),
        GlobalManager.jsonDomainVersion("zeit"),
        GlobalManager.jsonDomainVersion("standort")
    )
    var users by remember(art) { mutableStateOf<List<All>>(emptyList()) }
    var activeLocations by remember(art) { mutableStateOf<List<All>>(emptyList()) }
    var selectedUserId by remember(art) { mutableStateOf("") }
    var akte by remember(selectedUserId) { mutableStateOf(PersonalAkte(userId = selectedUserId)) }
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf("") }
    var showQrDialog by remember(selectedUserId) { mutableStateOf(false) }
    var vacationAllowance by remember(selectedUserId) { mutableStateOf(Urlaubsanspruch()) }
    val currentYear = kotlin.js.Date().getFullYear().toInt()

    suspend fun reloadUsers() {
        loading = true
        users = PersonalakteApi.loadUsers(art).sortedWith(
            compareBy<All> { personalUserName(it).lowercase() }.thenBy { it.id }
        )
        if (selectedUserId.isBlank() || users.none { it.id == selectedUserId }) {
            selectedUserId = users.firstOrNull()?.id.orEmpty()
        }
        loading = false
    }

    suspend fun reloadActiveLocations() {
        activeLocations = PersonalakteApi.loadActiveLocations()
            .sortedBy { it.standort.ifBlank { it.id }.lowercase() }
    }

    suspend fun reloadAkte(userId: String) {
        if (userId.isBlank()) {
            akte = PersonalAkte()
            return
        }
        akte = PersonalakteApi.loadAkte(userId).let { loaded ->
            loaded.copy(
                userId = userId,
                personal = personalDefaults(users.firstOrNull { it.id == userId }) + loaded.personal,
                vertrag = contractDefaults(users.firstOrNull { it.id == userId }) + loaded.vertrag,
                lohnbuchhaltung = payrollDefaults(users.firstOrNull { it.id == userId }) + loaded.lohnbuchhaltung,
                bank = bankDefaults(users.firstOrNull { it.id == userId }) + loaded.bank
            )
        }
    }

    LaunchedEffect(art, refreshVersion) {
        reloadUsers()
        reloadActiveLocations()
    }
    LaunchedEffect(selectedUserId, users, refreshVersion) { reloadAkte(selectedUserId) }
    LaunchedEffect(selectedUserId, saving, refreshVersion) {
        if (selectedUserId.isNotBlank() && !saving) {
            vacationAllowance = UrlaubsApi.loadAllowance(
                selectedUserId,
                kotlin.js.Date().getFullYear().toInt()
            )
        }
    }

    val selectedUser = users.firstOrNull { it.id == selectedUserId }
    val userName = personalUserName(selectedUser)
    var qrPreview by remember(selectedUserId) { mutableStateOf("") }
    val displayedAkte = akte
    LaunchedEffect(selectedUserId, selectedUser, displayedAkte.qrCode) {
        if (selectedUserId.isBlank()) {
            qrPreview = ""
        } else if (displayedAkte.qrCode.isNotBlank()) {
            qrPreview = displayedAkte.qrCode
        } else {
            qrPreview = akte.withQrForUser(selectedUser).qrCode
        }
    }
    val viewingSnapshot = false
    val employmentStatus = akte.vertrag["status"].orEmpty()
        .ifBlank { selectedUser?.status.orEmpty() }
        .trim()
    val selectedLocation = remember(activeLocations, displayedAkte.vertrag["standort"]) {
        personalLocationForValue(activeLocations, displayedAkte.vertrag["standort"].orEmpty())
    }
    val departmentOptions = remember(selectedLocation) {
        selectedLocation?.let(::personalDepartmentOptions).orEmpty()
    }
    val contractDropdownValues = remember(users, activeLocations, departmentOptions) {
        buildMap {
            put(
                "vorgesetzter",
                users
                    .map(::personalUserName)
                    .filter { it.isNotBlank() }
                    .distinct()
            )
            put(
                "standort",
                activeLocations
                    .map { it.standort.ifBlank { it.id } }
                    .filter { it.isNotBlank() }
                    .distinct()
            )
            put("abteilung", departmentOptions)
        }
    }
    fun updateSection(section: String, key: String, value: String) {
        if (viewingSnapshot) return
        akte = when (section) {
            "personal" -> akte.copy(personal = akte.personal + (key to value))
            "vertrag" -> akte.copy(vertrag = akte.vertrag + (key to value))
            "lohnbuchhaltung" -> akte.copy(lohnbuchhaltung = akte.lohnbuchhaltung + (key to value))
            "bank" -> akte.copy(bank = akte.bank + (key to value))
            else -> akte
        }
    }

    fun save() {
        if (saving || selectedUserId.isBlank() || viewingSnapshot) return
        saving = true
        message = ""
        scope.launch {
            val nextAkte = akte.withQrForUser(selectedUser)
            qrPreview = nextAkte.qrCode
            val saved = PersonalakteApi.saveAkte(nextAkte.copy(userId = selectedUserId))
            message = if (saved) "Personalverwaltung gespeichert." else "Personalverwaltung konnte nicht gespeichert werden."
            saving = false
        }
    }

    fun commitTable(table: String, columns: List<PersonalAkteTableColumn>, committedRows: List<Pair<String, List<String>>>) {
        if (viewingSnapshot) return
        val rows = committedRows.map { (id, values) ->
            PersonalAkteRow(
                id = id,
                values = columns.mapIndexed { index, column ->
                    column.key to values.getOrNull(index).orEmpty()
                }.toMap()
            )
        }
        val nextAkte = when (table) {
            "notfallkontakte" -> akte.copy(notfallkontakte = rows)
            "gehalt" -> akte.copy(gehalt = rows)
            "bonus" -> akte.copy(bonus = rows)
            "benefits" -> akte.copy(benefits = rows)
            else -> akte
        }.copy(userId = selectedUserId)
        akte = nextAkte
        if (selectedUserId.isNotBlank()) {
            saving = true
            message = ""
            scope.launch {
                val nextWithQr = nextAkte.withQrForUser(selectedUser)
                akte = nextWithQr
                qrPreview = nextWithQr.qrCode
                val saved = PersonalakteApi.saveAkte(nextWithQr)
                message = if (saved) "Personalverwaltung gespeichert." else "Personalverwaltung konnte nicht gespeichert werden."
                saving = false
            }
        }
    }

    fun snapshotRow(kind: String, date: String, source: PersonalAkte): PersonalAkteRow {
        val snapshot = source.copy(userId = selectedUserId, beschaeftigungsverlauf = emptyList())
        val json = personalAkteSnapshotJson.encodeToString(PersonalAkte.serializer(), snapshot)
        val contract = source.vertrag
        val employmentStart = contract["eintrittsdatum"].orEmpty()
        val employmentEnd = contract["vertragsende"].orEmpty().ifBlank { date }
        val label = listOf(
            personalDisplayDate(date),
            personalEmploymentPeriodLabel(employmentStart, employmentEnd),
            contract["status"].orEmpty()
        ).filter { it.isNotBlank() }.joinToString(" · ")
        return PersonalAkteRow(
            id = "verlauf_${kotlin.js.Date().getTime().toLong()}",
            values = mapOf(
                "typ" to kind,
                "datum" to date,
                "label" to label,
                "status" to contract["status"].orEmpty(),
                "vertragstyp" to contract["vertragstyp"].orEmpty(),
                "wochenstunden" to contract["wochenstunden"].orEmpty(),
                "start" to employmentStart,
                "ende" to employmentEnd,
                "snapshot" to json
            )
        )
    }

    fun saveAkte(nextAkte: PersonalAkte, successMessage: String) {
        if (saving || selectedUserId.isBlank()) return
        saving = true
        message = ""
        scope.launch {
            val nextWithQr = nextAkte.copy(userId = selectedUserId).withQrForUser(selectedUser)
            akte = nextWithQr
            qrPreview = nextWithQr.qrCode
            val saved = PersonalakteApi.saveAkte(nextWithQr)
            message = if (saved) successMessage else "Personalverwaltung konnte nicht gespeichert werden."
            saving = false
        }
    }

    Style(PersonalverwaltungStyles)
    Div({ classes(PersonalverwaltungStyles.page) }) {
        Div({ classes(PersonalverwaltungStyles.shell) }) {
            Div({ classes(PersonalverwaltungStyles.topbar) }) {
                Div({ classes(PersonalverwaltungStyles.employeeHeader) }) {
                    UserAvatarCircle(user = selectedUser, fallbackLabel = userName, sizePx = 72)
                    Div({ classes(PersonalverwaltungStyles.employeeText) }) {
                        AppDropdown(
                            options = users.sortedBy { personalUserName(it).lowercase() }
                                .map { All(key = it.id, label = personalUserName(it).ifBlank { it.id }) },
                            selectedValue = selectedUserId.ifBlank { null },
                            onChange = { selectedUserId = it },
                            placeholder = "Personalverwaltung",
                            enabled = !loading && users.isNotEmpty(),
                            appearance = AppDropdownAppearance.Heading,
                            menuMode = org.example.project.settings.AppDropdownMenuMode.Overlay,
                            attrs = { attr("aria-label", "Mitarbeiter auswählen") }
                        )
                    }
                }
                Div({ classes(PersonalverwaltungStyles.userSelect) }) {
                    LumaticaButton(
                        label = if (saving) "Speichern..." else "Speichern",
                        onClick = ::save,
                        variant = LumaticaButtonVariant.Action,
                        enabled = !viewingSnapshot,
                        heightPx = 36
                    )
                    LumaticaButton(
                        label = "MA QR",
                        onClick = { showQrDialog = true },
                        variant = LumaticaButtonVariant.Standard,
                        enabled = selectedUserId.isNotBlank(),
                        heightPx = 36,
                        content = {
                            Img(src = "images/qr.svg", attrs = {
                                classes(PersonalverwaltungStyles.qrButtonIcon)
                            })
                            Text("MA QR")
                        }
                    )
                }
            }

            if (message.isNotBlank()) {
                Span({ classes(PersonalverwaltungStyles.message) }) { Text(message) }
            }
            if (viewingSnapshot) {
                Span({ classes(PersonalverwaltungStyles.message) }) { Text("Snapshot-Ansicht: Änderungen sind deaktiviert.") }
            }


            if (showQrDialog) {
                PersonalQrDialog(
                    qrCode = qrPreview.ifBlank { displayedAkte.qrCode },
                    label = personalQrDisplayName(selectedUser),
                    onClose = { showQrDialog = false }
                )
            }


            Div({ classes(PersonalverwaltungStyles.content) }) {
                Div({ classes(PersonalverwaltungStyles.akteSurface) }) {
                    PersonalFormSection(
                        title = "Persönliche Informationen",
                        groups = listOf(
                            "Name" to listOf("anrede", "titel", "vorname", "nachname"),
                            "Adresse" to listOf("strasse", "hausnummer", "plz", "ort", "land"),
                            "Kontakt" to listOf("email", "telefon", "mobil", "emailPrivat", "telefonPrivat", "mobilPrivat")
                        ),
                        labels = personalLabels,
                        values = displayedAkte.personal,
                        section = "personal",
                        onChange = ::updateSection
                    )
                    PersonalFormSection(
                        title = "Mitarbeiterzuordnung",
                        groups = listOf(
                            "Bereich & Abteilung" to listOf("firma", "position", "standort", "abteilung", "personalnummer"),
                            "Arbeitsberechtigungen" to listOf("vertragstyp", "vorgesetzter", "wochenstunden", "homeofficeBerechtigt", "aussendienstBerechtigt")
                        ),
                        labels = contractLabels,
                        values = displayedAkte.vertrag,
                        section = "vertrag",
                        dropdownValues = contractDropdownValues,
                        onChange = ::updateSection
                    )

                    PersonalCoreMetricsCard(
                        allowance = vacationAllowance,
                        onChange = { field, value ->
                            if (!viewingSnapshot && selectedUserId.isNotBlank()) {
                                val next = when (field) {
                                    "resturlaub" -> vacationAllowance.copy(resturlaub = value.replace(",", ".").toDoubleOrNull() ?: 0.0)
                                    "jahresurlaub" -> vacationAllowance.copy(jahresurlaub = value.replace(",", ".").toDoubleOrNull() ?: 0.0)
                                    "arbeitszeitTaeglich" -> vacationAllowance.copy(arbeitszeitTaeglich = value.replace(",", ".").toDoubleOrNull() ?: 0.0)
                                    "arbeitszeitMonatlich" -> vacationAllowance.copy(arbeitszeitMonatlich = value.replace(",", ".").toDoubleOrNull() ?: 0.0)
                                    "zeitkonto" -> vacationAllowance.copy(zeitkonto = value.replace(",", ".").toDoubleOrNull() ?: 0.0)
                                    "zeitreserve" -> vacationAllowance.copy(zeitreserve = value.replace(",", ".").toDoubleOrNull()?.coerceAtLeast(0.0) ?: 0.0)
                                    else -> vacationAllowance
                                }
                                vacationAllowance = next
                                scope.launch {
                                    vacationAllowance = UrlaubsApi.saveAllowance(
                                        selectedUserId,
                                        currentYear,
                                        next.jahresurlaub,
                                        next.resturlaub,
                                        next.arbeitszeitTaeglich,
                                        next.arbeitszeitMonatlich,
                                        next.zeitkonto,
                                        next.zeitreserve
                                    ) ?: UrlaubsApi.loadAllowance(selectedUserId, currentYear)
                                }
                            }
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun PersonalCoreMetricsCard(
    allowance: Urlaubsanspruch,
    onChange: (String, String) -> Unit
) {
    Div({ classes(PersonalverwaltungStyles.sectionBlock) }) {
        Div({ classes(PersonalverwaltungStyles.sectionTitle) }) {
            Text("MA Kontingente")
        }
        Div({ classes(PersonalverwaltungStyles.groupBlock) }) {
            Span({ classes(PersonalverwaltungStyles.groupTitle) }) { Text("Urlaub") }
            Div({ classes(PersonalverwaltungStyles.metricGrid) }) {
                Div({ classes(PersonalverwaltungStyles.fieldRow) }) {
                    Span({ classes(PersonalverwaltungStyles.fieldLabel) }) { Text("Urlaubsanspruch") }
                    PersonalFieldInput(
                        field = "jahresurlaub",
                        value = personalMetricNumber(allowance.jahresurlaub),
                        section = "kontingente",
                        onChange = { _, field, value -> onChange(field, value) }
                    )
                }
                PersonalMetric(label = "Genommen", value = personalMetricNumber(allowance.urlaubGenommen))
                PersonalMetric(label = "Reserviert", value = personalMetricNumber(allowance.urlaubReserviert))
                PersonalMetric(label = "Verfügbar", value = personalMetricNumber(allowance.verfuegbar))
                Div({ classes(PersonalverwaltungStyles.fieldRow) }) {
                    Span({ classes(PersonalverwaltungStyles.fieldLabel) }) { Text("Resturlaub aus Vorjahr") }
                    PersonalFieldInput(
                        field = "resturlaub",
                        value = personalMetricNumber(allowance.resturlaub),
                        section = "kontingente",
                        onChange = { _, field, value -> onChange(field, value) }
                    )
                }
                PersonalMetric(
                    label = "Verfällt am",
                    value = personalDisplayDate(allowance.resturlaubVerfaelltAm).ifBlank { "31.03." }
                )
            }
        }
        Div({ classes(PersonalverwaltungStyles.groupBlock) }) {
            Span({ classes(PersonalverwaltungStyles.groupTitle) }) { Text("Zeit") }
            Div({ classes(PersonalverwaltungStyles.metricGrid) }) {
                PersonalMetric(
                    label = "Krankheitstage dieses Jahr",
                    value = allowance.krankheitstage.toString()
                )
                Div({ classes(PersonalverwaltungStyles.fieldRow) }) {
                    Span({ classes(PersonalverwaltungStyles.fieldLabel) }) { Text("Tagesarbeitszeit") }
                    PersonalFieldInput(
                        field = "arbeitszeitTaeglich",
                        value = personalMetricNumber(allowance.arbeitszeitTaeglich),
                        section = "kontingente",
                        onChange = { _, field, value -> onChange(field, value) }
                    )
                }
                Div({ classes(PersonalverwaltungStyles.fieldRow) }) {
                    Span({ classes(PersonalverwaltungStyles.fieldLabel) }) { Text("Arbeitszeit Monatlich") }
                    PersonalFieldInput(
                        field = "arbeitszeitMonatlich",
                        value = personalMetricNumber(allowance.arbeitszeitMonatlich),
                        section = "kontingente",
                        onChange = { _, field, value -> onChange(field, value) }
                    )
                }
                Div({ classes(PersonalverwaltungStyles.fieldRow) }) {
                    Span({ classes(PersonalverwaltungStyles.fieldLabel) }) { Text("Zeitkonto") }
                    PersonalFieldInput(
                        field = "zeitkonto",
                        value = personalMetricNumber(allowance.zeitkonto),
                        section = "kontingente",
                        onChange = { _, field, value -> onChange(field, value) }
                    )
                }
                Div({ classes(PersonalverwaltungStyles.fieldRow) }) {
                    Span({ classes(PersonalverwaltungStyles.fieldLabel) }) { Text("Zeitreserve") }
                    PersonalFieldInput(
                        field = "zeitreserve",
                        value = personalMetricNumber(allowance.zeitreserve),
                        section = "kontingente",
                        onChange = { _, field, value -> onChange(field, value) }
                    )
                }
                PersonalMetric(
                    label = "Reservierte Überstunden",
                    value = personalMetricNumber(allowance.reservierteUeberstunden)
                )
                PersonalMetric(
                    label = "Für Freizeitausgleich verfügbar",
                    value = personalMetricNumber(allowance.zeitkontoVerfuegbar)
                )
            }
        }
    }
}

private fun personalSignedHours(value: Double): String =
    when {
        value > 0.0 -> "+${personalMetricNumber(value)} Std."
        value < 0.0 -> "${personalMetricNumber(value)} Std."
        else -> "0 Std."
    }

@Composable
private fun PersonalMetric(label: String, value: String) {
    Div({ classes(PersonalverwaltungStyles.fieldRow) }) {
        Span({ classes(PersonalverwaltungStyles.fieldLabel) }) { Text(label) }
        Span({ classes(PersonalverwaltungStyles.metricValue) }) { Text(value) }
    }
}

private fun personalMetricNumber(value: Double): String =
    if (value % 1.0 == 0.0) value.toInt().toString() else value.toString().replace(".", ",")

@Composable
private fun PersonalQrDialog(qrCode: String, label: String, onClose: () -> Unit) {
    PersonalDialogFrame(title = "MA QR") {
        Div({ classes(PersonalverwaltungStyles.qrDialogContent) }) {
        if (qrCode.isNotBlank()) {
                Img(src = qrCode, attrs = { classes(PersonalverwaltungStyles.qrDialogImage) })
        } else {
                Div({ classes(PersonalverwaltungStyles.qrDialogPlaceholder) }) { Text("QR") }
            }
            Span({ classes(PersonalverwaltungStyles.qrDialogLabel) }) { Text(label.ifBlank { "-" }) }
        }
        Div({ classes(PersonalverwaltungStyles.dialogActions) }) {
            LumaticaButton(label = "Schließen", onClick = onClose, heightPx = 34)
        }
    }
}

@Composable
private fun PersonalFormSection(
    title: String,
    groups: List<Pair<String, List<String>>>,
    labels: Map<String, String>,
    values: Map<String, String>,
    section: String,
    dropdownValues: Map<String, List<String>> = emptyMap(),
    onChange: (String, String, String) -> Unit
) {
    Div({ classes(PersonalverwaltungStyles.sectionBlock) }) {
        Div({ classes(PersonalverwaltungStyles.sectionTitle) }) { Text(title) }
        groups.forEach { (groupTitle, fields) ->
            Div({ classes(PersonalverwaltungStyles.groupBlock) }) {
                if (groupTitle == "Kontakt") {
                    PersonalContactBlock(
                        values = values,
                        section = section,
                        onChange = onChange
                    )
                } else {
                    Div({ classes(PersonalverwaltungStyles.fieldGrid) }) {
                        fields.forEach { field ->
                            if (field == "kennzeichen" && !personalChecked(values["firmenfahrzeug"].orEmpty())) return@forEach
                            Div({ classes(PersonalverwaltungStyles.fieldRow) }) {
                                Span({ classes(PersonalverwaltungStyles.fieldLabel) }) { Text(labels[field] ?: field) }
                                PersonalFieldInput(
                                    field = field,
                                    value = values[field].orEmpty(),
                                    section = section,
                                    suppliedDropdownValues = dropdownValues[field],
                                    onChange = onChange
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun PersonalContactBlock(
    values: Map<String, String>,
    section: String,
    onChange: (String, String, String) -> Unit
) {
    val rows = listOf(
        listOf(
            "email" to "E-Mail (Geschäftlich)",
            "telefon" to "Telefon (Geschäftlich)",
            "mobil" to "Mobil (Geschäftlich)"
        ),
        listOf(
            "emailPrivat" to "E-Mail (Privat)",
            "telefonPrivat" to "Telefon (Privat)",
            "mobilPrivat" to "Mobil (Privat)"
        )
    )
    Div({ classes(PersonalverwaltungStyles.contactBlock) }) {
        rows.forEach { fields ->
            Div({ classes(PersonalverwaltungStyles.contactRow) }) {
                fields.forEach { (field, label) ->
                    Div({ classes(PersonalverwaltungStyles.fieldRow) }) {
                        Span({ classes(PersonalverwaltungStyles.fieldLabel) }) { Text(label) }
                        Input(InputType.Text, attrs = {
                            classes(PersonalverwaltungStyles.input)
                            value(values[field].orEmpty())
                            placeholder("")
                            onInput { onChange(section, field, it.value) }
                        })
                    }
                }
            }
        }
    }
}

@Composable
private fun PersonalFieldInput(
    field: String,
    value: String,
    section: String,
    suppliedDropdownValues: List<String>? = null,
    onChange: (String, String, String) -> Unit
) {
    val dropdownValues = suppliedDropdownValues ?: when (field) {
        "land" -> personalCountries
        "nationalitaet" -> personalNationalities
        "hauptbeschaeftigung", "firmenfahrzeug", "kirchensteuerpflichtig", "homeofficeBerechtigt", "aussendienstBerechtigt" -> listOf("Ja", "Nein")
        else -> personalDropdownValues[field]
    }
    when {
        dropdownValues != null -> {
            val dropdownPlaceholder = when {
                field == "abteilung" && dropdownValues.isEmpty() -> "Keine Abteilungen"
                else -> ""
            }
            AppDropdown(
                options = dropdownValues.map { All(key = it, label = it) },
                selectedValue = value.ifBlank { null },
                onChange = { onChange(section, field, it) },
                placeholder = dropdownPlaceholder,
                appearance = AppDropdownAppearance.Surface,
                attrs = {
                    style {
                        minHeight(38.px)
                        width(100.percent)
                    }
                }
            )
        }
        field in personalCheckboxFields -> {
            Div({ classes(PersonalverwaltungStyles.checkboxField) }) {
                LumaticaCheckbox(
                    checked = personalChecked(value),
                    onChange = { checked -> onChange(section, field, if (checked) "Ja" else "Nein") },
                    fontSizePx = 20
                )
            }
        }
        field in personalDateFields -> {
            var calendarOpen by remember(field, section) { mutableStateOf(false) }
            Div({ classes(PersonalverwaltungStyles.dateField) }) {
                Input(InputType.Text, attrs = {
                    classes(PersonalverwaltungStyles.input)
                    value(personalDisplayDate(value))
                    placeholder("")
                    onClick {
                        calendarOpen = true
                    }
                    onInput { onChange(section, field, personalInputDate(it.value)) }
                })
                Img(src = "images/kalender.svg", attrs = {
                    classes(PersonalverwaltungStyles.dateIcon)
                    onClick {
                        calendarOpen = !calendarOpen
                    }
                })
                if (calendarOpen) {
                    Div({
                        classes(PersonalverwaltungStyles.dateCalendarPopup)
                        onClick { it.stopPropagation() }
                    }) {
                        EngineMonthCalendar(
                            selectedDate = personalCalendarDate(value),
                            onSelectDate = {
                                onChange(section, field, it)
                                calendarOpen = false
                            },
                            showFooter = false,
                            useWeekBorderLayout = false,
                            showSelectedDayBorder = false
                        )
                    }
                }
            }
        }
        else -> {
            Input(InputType.Text, attrs = {
                classes(PersonalverwaltungStyles.input)
                value(value)
                placeholder("")
                onInput { onChange(section, field, it.value) }
            })
        }
    }
}

private fun personalChecked(value: String): Boolean =
    value.equals("ja", ignoreCase = true) ||
        value.equals("true", ignoreCase = true) ||
        value == "\u2705"

private fun personalCalendarDate(value: String): String {
    val normalized = value.trim()
    return when {
        normalized.matches(Regex("\\d{4}-\\d{2}-\\d{2}")) -> normalized
        normalized.matches(Regex("\\d{2}\\.\\d{2}\\.\\d{4}")) -> {
            val parts = normalized.split(".")
            "${parts[2]}-${parts[1]}-${parts[0]}"
        }
        else -> kalenderTodayIsoDate()
    }
}

private fun personalDisplayDate(value: String): String {
    val normalized = value.trim()
    return when {
        normalized.matches(Regex("\\d{4}-\\d{2}-\\d{2}")) -> {
            val parts = normalized.split("-")
            "${parts[2]}.${parts[1]}.${parts[0]}"
        }
        else -> value
    }
}

private fun personalInputDate(value: String): String {
    val normalized = value.trim()
    return when {
        normalized.matches(Regex("\\d{2}\\.\\d{2}\\.\\d{4}")) -> {
            val parts = normalized.split(".")
            "${parts[2]}-${parts[1]}-${parts[0]}"
        }
        else -> value
    }
}

private fun personalSnapshotLabel(row: PersonalAkteRow): String {
    val snapshotContract = runCatching {
        personalAkteSnapshotJson
            .decodeFromString(PersonalAkte.serializer(), row.values["snapshot"].orEmpty())
            .vertrag
    }.getOrDefault(emptyMap())
    val employmentStart = row.values["start"].orEmpty()
        .ifBlank { snapshotContract["eintrittsdatum"].orEmpty() }
    val employmentEnd = row.values["ende"].orEmpty()
        .ifBlank { snapshotContract["vertragsende"].orEmpty() }
        .ifBlank { row.values["datum"].orEmpty() }
    val status = row.values["status"].orEmpty()
        .ifBlank { snapshotContract["status"].orEmpty() }
    val structuredLabel = listOf(
        personalDisplayDate(row.values["datum"].orEmpty()),
        personalEmploymentPeriodLabel(employmentStart, employmentEnd),
        status
    ).filter { it.isNotBlank() }.joinToString(" · ")
    return structuredLabel.ifBlank { row.values["label"].orEmpty().ifBlank { row.id } }
}

private fun personalEmploymentPeriodLabel(start: String, end: String): String {
    val displayStart = personalDisplayDate(start)
    val displayEnd = personalDisplayDate(end)
    return when {
        displayStart.isNotBlank() && displayEnd.isNotBlank() ->
            "Arbeitsverhältnis von $displayStart bis $displayEnd"
        displayStart.isNotBlank() -> "Arbeitsverhältnis ab $displayStart"
        displayEnd.isNotBlank() -> "Arbeitsverhältnis bis $displayEnd"
        else -> "Arbeitsverhältnis"
    }
}

/* Employment termination and rehire dialogs removed: Personalakte is attendance-only. */
/*
@Composable
private fun PersonalTerminationDialog(
    date: String,
    onDateChange: (String) -> Unit,
    onCancel: () -> Unit,
    onConfirm: () -> Unit
) {
    var confirmCountdown by remember { mutableStateOf(5) }

    LaunchedEffect(Unit) {
        while (confirmCountdown > 0) {
            delay(1000)
            confirmCountdown -= 1
        }
    }
    PersonalDialogFrame(title = "Verhältnis kündigen") {
        Span({ classes(PersonalverwaltungStyles.dialogWarning) }) {
            Text("Sind Sie sicher, dass Sie das Vertragsverh\u00e4ltnis k\u00fcndigen wollen?")
        }
        Div({ classes(PersonalverwaltungStyles.dialogField) }) {
            Span({ classes(PersonalverwaltungStyles.fieldLabel) }) { Text("Kündigungsdatum") }
            PersonalFieldInput(
                field = "vertragsende",
                value = date,
                section = "vertrag",
                onChange = { _, _, value -> onDateChange(value) }
            )
        }
        Div({ classes(PersonalverwaltungStyles.dialogActions) }) {
            LumaticaButton(label = "Abbrechen", onClick = onCancel, heightPx = 34)
            LumaticaButton(
                label = if (confirmCountdown > 0) "Kündigen ($confirmCountdown)" else "Kündigen",
                onClick = { if (confirmCountdown == 0) onConfirm() },
                variant = LumaticaButtonVariant.Action,
                enabled = confirmCountdown == 0,
                heightPx = 34
            )
        }
    }
}

@Composable
private fun PersonalRehireDialog(
    start: String,
    type: String,
    hours: String,
    probezeit: String,
    onStartChange: (String) -> Unit,
    onTypeChange: (String) -> Unit,
    onHoursChange: (String) -> Unit,
    onProbezeitChange: (String) -> Unit,
    onCancel: () -> Unit,
    onConfirm: () -> Unit
) {
    PersonalDialogFrame(title = "Mitarbeiter wiedereinstellen") {
        Div({ classes(PersonalverwaltungStyles.dialogField) }) {
            Span({ classes(PersonalverwaltungStyles.fieldLabel) }) { Text("Startdatum") }
            PersonalFieldInput(
                field = "eintrittsdatum",
                value = start,
                section = "vertrag",
                onChange = { _, _, value -> onStartChange(value) }
            )
        }
        Div({ classes(PersonalverwaltungStyles.dialogField) }) {
            Span({ classes(PersonalverwaltungStyles.fieldLabel) }) { Text("Art der Beschäftigung") }
            AppDropdown(
                options = personalEmploymentTypes.map { All(key = it, label = it) },
                selectedValue = type.ifBlank { null },
                onChange = onTypeChange,
                placeholder = "Art wählen",
                appearance = AppDropdownAppearance.Surface,
                attrs = {
                    style {
                        width(100.percent)
                        minHeight(38.px)
                    }
                }
            )
        }
        Div({ classes(PersonalverwaltungStyles.dialogField) }) {
            Span({ classes(PersonalverwaltungStyles.fieldLabel) }) { Text("Wochenstunden") }
            Input(InputType.Text, attrs = {
                classes(PersonalverwaltungStyles.input)
                value(hours)
                onInput { onHoursChange(it.value) }
            })
        }
        Div({ classes(PersonalverwaltungStyles.dialogField) }) {
            Span({ classes(PersonalverwaltungStyles.fieldLabel) }) { Text("Dauer Probezeit") }
            AppDropdown(
                options = (1..12).map { "$it Monat${if (it == 1) "" else "e"}" }.map { All(key = it, label = it) },
                selectedValue = probezeit.ifBlank { null },
                onChange = onProbezeitChange,
                placeholder = "Probezeit wählen",
                appearance = AppDropdownAppearance.Surface,
                attrs = {
                    style {
                        width(100.percent)
                        minHeight(38.px)
                    }
                }
            )
        }
        Div({ classes(PersonalverwaltungStyles.dialogActions) }) {
            LumaticaButton(label = "Abbrechen", onClick = onCancel, heightPx = 34)
            LumaticaButton(label = "Wiedereinstellen", onClick = onConfirm, variant = LumaticaButtonVariant.Action, heightPx = 34)
        }
    }
}

@Composable
*/
@Composable
private fun PersonalDialogFrame(
    title: String,
    content: @Composable () -> Unit
) {
    Div({ classes(PersonalverwaltungStyles.dialogOverlay) }) {
        Div({ classes(PersonalverwaltungStyles.dialogBox) }) {
            H3({ classes(PersonalverwaltungStyles.dialogTitle) }) { Text(title) }
            content()
        }
    }
}

private fun normalizeEmploymentRows(rows: List<PersonalAkteRow>): List<PersonalAkteRow> {
    val activeIndex = rows.indexOfLast { personalChecked(it.values["aktiv"].orEmpty()) }
    return rows.mapIndexed { index, row ->
        val activeValue = if (index == activeIndex) "\u2705" else "\u2B1C"
        row.copy(values = row.values + ("aktiv" to activeValue))
    }
}

private fun PersonalAkte.withActiveEmploymentContract(): PersonalAkte {
    val active = beschaeftigungsverlauf.lastOrNull { personalChecked(it.values["aktiv"].orEmpty()) }
        ?: return this
    val contractUpdates = mapOf(
        "eintrittsdatum" to active.values["start"].orEmpty(),
        "vertragsende" to active.values["ende"].orEmpty(),
        "vertragstyp" to active.values["vertragstyp"].orEmpty(),
        "status" to active.values["status"].orEmpty(),
        "wochenstunden" to active.values["wochenstunden"].orEmpty(),
        "position" to active.values["position"].orEmpty(),
        "kostenstelle" to active.values["kostenstelle"].orEmpty()
    ).filterValues { it.isNotBlank() }
    if (contractUpdates.isEmpty()) return this
    return copy(vertrag = vertrag + contractUpdates)
}

@Composable
private fun PersonalAkteTabularSection(
    title: String,
    columns: List<PersonalAkteTableColumn>,
    rows: List<PersonalAkteRow>,
    screenKey: String,
    calendarColumns: Set<String> = emptySet(),
    toggleColumns: Set<String> = emptySet(),
    dropdownOptions: Map<String, List<String>> = emptyMap(),
    createRow: (List<PersonalAkteTableColumn>) -> List<String> = { columns -> MutableList(columns.size) { "" } },
    onCommit: (List<Pair<String, List<String>>>) -> Unit
) {
    Div({ classes(PersonalverwaltungStyles.tableBlock) }) {
        TabularScreen(
            headers = columns.map { it.label },
            data = rows.mapIndexed { index, row ->
                row.id.ifBlank { (index + 1).toString() } to columns.map { column -> row.values[column.key].orEmpty() }
            },
            onCommit = onCommit,
            screenKey = screenKey,
            title = title,
            createButtonLabel = "Erstellen",
            saveButtonLabel = "Speichern",
            calendarColumns = calendarColumns,
            toggleColumns = toggleColumns,
            dropdownOptions = dropdownOptions,
            allowImportButton = false,
            allowExportButton = false,
            allowSettingsButton = false,
            allowRefreshButton = false,
            allowSearchButton = false,
            showToolbarExtras = false,
            showFilterRow = false,
            showFillerRows = false,
            smartMode = true,
            includeMetadata = false,
            useUserSettings = false,
            dataColumnWidthCss = "minmax(150px, 1fr)",
            verticalScroll = false,
            createRow = { createRow(columns) }
        )
    }
}

private data class PersonalAkteTableColumn(
    val key: String,
    val label: String
)

private val notfallkontaktColumns = listOf(
    PersonalAkteTableColumn("name", "Name"),
    PersonalAkteTableColumn("telefonnummer", "Telefonnummer")
)

private val gehaltColumns = listOf(
    PersonalAkteTableColumn("start", "Start"),
    PersonalAkteTableColumn("ende", "Ende"),
    PersonalAkteTableColumn("bruttogehalt", "Bruttogehalt"),
    PersonalAkteTableColumn("gehaltstyp", "Gehaltstyp"),
    PersonalAkteTableColumn("wochenstunden", "Wochenstunden"),
    PersonalAkteTableColumn("notizen", "Notizen")
)

private val beschaeftigungsverlaufColumns = listOf(
    PersonalAkteTableColumn("aktiv", "Aktiv"),
    PersonalAkteTableColumn("start", "Start"),
    PersonalAkteTableColumn("ende", "Ende"),
    PersonalAkteTableColumn("vertragstyp", "Vertragstyp"),
    PersonalAkteTableColumn("status", "Zustand"),
    PersonalAkteTableColumn("wochenstunden", "Wochenstunden"),
    PersonalAkteTableColumn("position", "Position"),
    PersonalAkteTableColumn("kostenstelle", "Kostenstelle"),
    PersonalAkteTableColumn("notiz", "Notiz")
)

private val bonusColumns = listOf(
    PersonalAkteTableColumn("datum", "Datum"),
    PersonalAkteTableColumn("betrag", "Betrag"),
    PersonalAkteTableColumn("typ", "Typ"),
    PersonalAkteTableColumn("text", "Text")
)

private val benefitColumns = listOf(
    PersonalAkteTableColumn("start", "Start"),
    PersonalAkteTableColumn("ende", "Ende"),
    PersonalAkteTableColumn("typ", "Typ"),
    PersonalAkteTableColumn("betrag", "Betrag"),
    PersonalAkteTableColumn("text", "Text")
)

private val personalEmploymentTypes = listOf("Vollzeit", "Teilzeit", "Ausbildung", "Aushilfe")

private val personalDropdownValues = mapOf(
    "geschlecht" to listOf("Männlich", "Weiblich", "Divers"),
    "land" to listOf("Deutschland", "Österreich", "Schweiz", "Niederlande", "Belgien", "Frankreich", "Polen", "Tschechien", "Italien", "Spanien"),
    "nationalitaet" to listOf("Deutsch", "Österreichisch", "Schweizerisch", "Niederländisch", "Belgisch", "Französisch", "Polnisch", "Tschechisch", "Italienisch", "Spanisch"),
    "status" to listOf("Aktiv", "Vorgemerkt", "Gekündigt"),
    "vertragstyp" to personalEmploymentTypes,
    "dauerProbezeit" to (1..12).map { "$it Monat${if (it == 1) "" else "e"}" }
)

private val personalCheckboxFields = emptySet<String>()

private val personalDateFields = setOf(
    "geburtsdatum",
    "eintrittsdatum",
    "vertragsende",
    "beginnAltersteilzeit",
    "endeAltersteilzeit"
)

private val personalCountries = listOf(
    "Deutschland", "Afghanistan", "Ägypten", "Albanien", "Algerien", "Andorra", "Angola", "Argentinien",
    "Armenien", "Australien", "Aserbaidschan", "Bahamas", "Bahrain", "Bangladesch", "Barbados",
    "Belgien", "Belarus", "Belize", "Benin", "Bhutan", "Bolivien", "Bosnien und Herzegowina",
    "Botswana", "Brasilien", "Brunei", "Bulgarien", "Burkina Faso", "Burundi", "Chile", "China",
    "Costa Rica", "Dänemark", "Dominikanische Republik", "Ecuador", "El Salvador", "Elfenbeinküste",
    "Eritrea", "Estland", "Finnland", "Frankreich", "Georgien", "Ghana", "Griechenland",
    "Großbritannien", "Guatemala", "Honduras", "Indien", "Indonesien", "Irak", "Iran", "Irland",
    "Island", "Israel", "Italien", "Japan", "Jemen", "Jordanien", "Kambodscha", "Kamerun", "Kanada",
    "Kasachstan", "Katar", "Kenia", "Kolumbien", "Kongo", "Kroatien", "Kuba", "Kuwait", "Laos",
    "Lettland", "Libanon", "Libyen", "Liechtenstein", "Litauen", "Luxemburg", "Malaysia", "Mali",
    "Malta", "Marokko", "Mexiko", "Moldau", "Monaco", "Mongolei", "Montenegro", "Mosambik",
    "Myanmar", "Namibia", "Nepal", "Neuseeland", "Nicaragua", "Niederlande", "Nigeria",
    "Nordmazedonien", "Norwegen", "Österreich", "Pakistan", "Panama", "Paraguay", "Peru",
    "Philippinen", "Polen", "Portugal", "Rumänien", "Russland", "Saudi-Arabien", "Schweden",
    "Schweiz", "Senegal", "Serbien", "Singapur", "Slowakei", "Slowenien", "Spanien", "Sri Lanka",
    "Südafrika", "Südkorea", "Syrien", "Tansania", "Thailand", "Tschechien", "Tunesien", "Türkei",
    "Uganda", "Ukraine", "Ungarn", "Uruguay", "USA", "Usbekistan", "Venezuela", "Vereinigte Arabische Emirate",
    "Vietnam", "Zypern"
)

private val personalNationalities = listOf(
    "Deutsch", "Afghanisch", "Ägyptisch", "Albanisch", "Algerisch", "Andorranisch", "Angolanisch",
    "Argentinisch", "Armenisch", "Australisch", "Aserbaidschanisch", "Bahrainisch", "Bangladeschisch",
    "Belgisch", "Belarussisch", "Beninisch", "Bolivianisch", "Bosnisch-herzegowinisch", "Botswanisch",
    "Brasilianisch", "Britisch", "Bulgarisch", "Burkinisch", "Burundisch", "Chilenisch", "Chinesisch",
    "Costa-ricanisch", "Dänisch", "Dominikanisch", "Ecuadorianisch", "Eritreisch", "Estnisch", "Finnisch",
    "Französisch", "Georgisch", "Ghanaisch", "Griechisch", "Guatemaltekisch", "Honduranisch", "Indisch",
    "Indonesisch", "Irakisch", "Iranisch", "Irisch", "Isländisch", "Israelisch", "Italienisch", "Japanisch",
    "Jemenitisch", "Jordanisch", "Kambodschanisch", "Kamerunisch", "Kanadisch", "Kasachisch", "Katarisch",
    "Kenianisch", "Kolumbianisch", "Kongolesisch", "Kroatisch", "Kubanisch", "Kuwaitisch", "Laotisch",
    "Lettisch", "Libanesisch", "Libysch", "Liechtensteinisch", "Litauisch", "Luxemburgisch", "Malaysisch",
    "Malisch", "Maltesisch", "Marokkanisch", "Mexikanisch", "Moldauisch", "Monegassisch", "Mongolisch",
    "Montenegrinisch", "Mosambikanisch", "Myanmarisch", "Namibisch", "Nepalesisch", "Neuseeländisch",
    "Nicaraguanisch", "Niederländisch", "Nigerianisch", "Nordmazedonisch", "Norwegisch", "Österreichisch",
    "Pakistanisch", "Panamaisch", "Paraguayisch", "Peruanisch", "Philippinisch", "Polnisch", "Portugiesisch",
    "Rumänisch", "Russisch", "Saudi-arabisch", "Schwedisch", "Schweizerisch", "Senegalesisch", "Serbisch",
    "Singapurisch", "Slowakisch", "Slowenisch", "Spanisch", "Sri-lankisch", "Südafrikanisch", "Südkoreanisch",
    "Syrisch", "Tansanisch", "Thailändisch", "Tschechisch", "Tunesisch", "Türkisch", "Ukrainisch",
    "Ungarisch", "Uruguayisch", "US-amerikanisch", "Usbekisch", "Venezolanisch", "Vietnamesisch", "Zyprisch"
)

private fun personalUserName(user: All?): String =
    user?.name?.ifBlank {
        listOf(user.vorname, user.nachname).filter { it.isNotBlank() }.joinToString(" ")
    }.orEmpty()

private fun personalQrDisplayName(user: All?): String =
    listOf(user?.vorname.orEmpty(), user?.nachname.orEmpty())
        .filter { it.isNotBlank() }
        .joinToString(" ")
        .ifBlank { personalUserName(user) }

private fun PersonalAkte.qrCodeTextForUser(user: All?): String =
    buildJsonObject {
        put("typ", "lumatica-personal-stempel")
        put("firma", vertrag["firma"].orEmpty().ifBlank { user?.firma.orEmpty() })
        put("standort", vertrag["standort"].orEmpty().ifBlank { user?.standort.orEmpty() })
        put("personalnummer", vertrag["personalnummer"].orEmpty().ifBlank { user?.personalnummer.orEmpty() })
        put("userId", userId.ifBlank { user?.id.orEmpty() })
        put("vorname", personal["vorname"].orEmpty().ifBlank { user?.vorname.orEmpty() })
        put("nachname", personal["nachname"].orEmpty().ifBlank { user?.nachname.orEmpty() })
    }.toString()

private suspend fun PersonalAkte.withQrForUser(user: All?): PersonalAkte {
    val qrText = qrCodeTextForUser(user)
    val label = personalQrDisplayName(user)
    val code = runCatching { personalQrCodeDataUrl(qrText) }.getOrDefault(qrCode)
    return copy(qrCodeText = qrText, qrCode = code, qrLabel = label)
}

private suspend fun personalQrCodeDataUrl(qrText: String): String {
    if (qrText.isBlank()) return ""
    val qrcode = js("typeof require !== 'undefined' ? require('qrcode') : null")
    val module = qrcode?.default ?: qrcode ?: error("qrcode not available")
    return module.toDataURL(qrText).unsafeCast<Promise<String>>().await()
}

private fun personalDefaults(user: All?): Map<String, String> = mapOf(
    "anrede" to user?.anrede.orEmpty(),
    "titel" to user?.titel.orEmpty(),
    "vorname" to user?.vorname.orEmpty(),
    "nachname" to user?.nachname.orEmpty(),
    "email" to user?.email.orEmpty(),
    "telefon" to user?.telefon.orEmpty(),
    "mobil" to user?.mobil.orEmpty(),
    "emailPrivat" to user?.emailPrivat.orEmpty(),
    "telefonPrivat" to user?.telefonPrivat.orEmpty().ifBlank { user?.privatnummer.orEmpty() },
    "mobilPrivat" to user?.mobilPrivat.orEmpty(),
    "strasse" to user?.strasse.orEmpty(),
    "hausnummer" to user?.hausnummer.orEmpty(),
    "plz" to user?.plz.orEmpty(),
    "ort" to user?.ort.orEmpty(),
    "land" to user?.land.orEmpty(),
    "geschlecht" to user?.geschlecht.orEmpty(),
    "geburtsdatum" to user?.geburtsdatum.orEmpty(),
    "geburtsort" to user?.geburtsort.orEmpty(),
    "nationalitaet" to user?.nationalitaet.orEmpty(),
    "familienstand" to user?.familienstand.orEmpty(),
    "kirchensteuerpflichtig" to user?.kirchensteuerpflichtig.orEmpty(),
    "hoechsterSchulabschluss" to user?.hoechsterSchulabschluss.orEmpty()
)

private fun contractDefaults(user: All?): Map<String, String> = mapOf(
    "firma" to user?.firma.orEmpty(),
    "position" to "",
    "abteilung" to user?.abteilung.orEmpty(),
    "standort" to user?.standort.orEmpty(),
    "personalnummer" to user?.personalnummer.orEmpty(),
    "status" to user?.status.orEmpty(),
    "eintrittsdatum" to user?.eintrittsdatum.orEmpty(),
    "vertragsende" to user?.vertragsende.orEmpty(),
    "vertragstyp" to user?.vertragstyp.orEmpty(),
    "beginnAltersteilzeit" to user?.beginnAltersteilzeit.orEmpty(),
    "endeAltersteilzeit" to user?.endeAltersteilzeit.orEmpty(),
    "dauerProbezeit" to user?.dauerProbezeit.orEmpty(),
    "kostenstelle" to user?.kostenstelle.orEmpty(),
    "vorgesetzter" to user?.vorgesetzter.orEmpty(),
    "wochenstunden" to user?.wochenstunden.orEmpty(),
    "urlaubstageProJahr" to user?.urlaubstageProJahr.orEmpty(),
    "resturlaubVorjahr" to user?.resturlaubVorjahr.orEmpty(),
    "homeofficeBerechtigt" to "Nein",
    "aussendienstBerechtigt" to "Nein"
)

private fun personalLocationForValue(locations: List<All>, value: String): All? {
    val clean = value.trim()
    if (clean.isBlank()) return null
    return locations.firstOrNull { location ->
        clean.equals(location.id.trim(), ignoreCase = true) ||
            clean.equals(location.standort.trim(), ignoreCase = true) ||
            clean.equals(location.bezeichnung.trim(), ignoreCase = true) ||
            clean.equals(location.name.trim(), ignoreCase = true)
    }
}

private fun personalDepartmentOptions(location: All): List<String> =
    location.abteilungen
        .mapNotNull { abteilung ->
            abteilung.name
                .ifBlank { abteilung.bezeichnung }
                .trim()
                .takeIf { it.isNotBlank() }
        }
        .distinct()
        .sortedBy { it.lowercase() }

private fun payrollDefaults(user: All?): Map<String, String> = mapOf(
    "krankenkasse" to user?.krankenkasse.orEmpty(),
    "versichertennummer" to user?.versichertennummer.orEmpty(),
    "zweitversicherung" to user?.zweitversicherung.orEmpty(),
    "nummerZweitversicherer" to user?.nummerZweitversicherer.orEmpty(),
    "behinderungsstatus" to user?.behinderungsstatus.orEmpty(),
    "sozialversicherungsnummer" to user?.sozialversicherungsnummer.orEmpty(),
    "steuerId" to user?.steuerId.orEmpty(),
    "steuerklasse" to user?.steuerklasse.orEmpty(),
    "kinderfreibetrag" to user?.kinderfreibetrag.orEmpty(),
    "betrieblicheAltersvorsorge" to user?.betrieblicheAltersvorsorge.orEmpty(),
    "hauptbeschaeftigung" to user?.hauptbeschaeftigung.orEmpty(),
    "zuschuesse" to user?.zuschuesse.orEmpty(),
    "firmenfahrzeug" to user?.firmenfahrzeug.orEmpty(),
    "kennzeichen" to user?.kennzeichen.orEmpty(),
    "insolvenzschutzNachweis" to user?.insolvenzschutzNachweis.orEmpty()
)

private fun bankDefaults(user: All?): Map<String, String> = mapOf(
    "kreditinstitut" to user?.kreditinstitut.orEmpty(),
    "kontoinhaber" to user?.kontoinhaber.orEmpty(),
    "iban" to user?.iban.orEmpty(),
    "bic" to user?.bic.orEmpty()
)

private val personalLabels = mapOf(
    "anrede" to "Anrede",
    "titel" to "Titel",
    "vorname" to "Vorname *",
    "nachname" to "Nachname",
    "strasse" to "Straße",
    "hausnummer" to "Hausnummer",
    "plz" to "PLZ",
    "ort" to "Ort",
    "land" to "Land",
    "email" to "E-Mail (Geschäftlich)",
    "telefon" to "Geschäftstelefon",
    "mobil" to "Mobiltelefon",
    "emailPrivat" to "E-Mail (Privat)",
    "telefonPrivat" to "Telefon (Privat)",
    "mobilPrivat" to "Mobil (Privat)",
    "geschlecht" to "Geschlecht",
    "geburtsdatum" to "Geburtsdatum",
    "geburtsort" to "Geburtsort",
    "nationalitaet" to "Nationalität",
    "familienstand" to "Familienstand",
    "kirchensteuerpflichtig" to "Kirchensteuerpflichtig",
    "hoechsterSchulabschluss" to "Höchster Schulabschluss"
)

private val contractLabels = mapOf(
    "firma" to "Gesellschaft",
    "position" to "Interne Position",
    "abteilung" to "Abteilung",
    "standort" to "Standort",
    "personalnummer" to "Personalnummer",
    "status" to "Zustand",
    "eintrittsdatum" to "Vertragsbeginn",
    "vertragsende" to "Vertragsende",
    "vertragstyp" to "Art der Beschäftigung",
    "beginnAltersteilzeit" to "Beginn der Altersteilzeit",
    "endeAltersteilzeit" to "Ende der Altersteilzeit",
    "dauerProbezeit" to "Dauer Probezeit",
    "kostenstelle" to "Kostenstelle",
    "vorgesetzter" to "Vorgesetzter",
    "wochenstunden" to "Wochenstunden",
    "urlaubstageProJahr" to "Urlaubsanspruch",
    "resturlaubVorjahr" to "Resturlaub aus Vorjahr",
    "homeofficeBerechtigt" to "Homeoffice berechtigt",
    "aussendienstBerechtigt" to "Außendienst berechtigt"
)

private val payrollLabels = mapOf(
    "krankenkasse" to "Krankenkasse",
    "versichertennummer" to "Versichertennummer",
    "zweitversicherung" to "Zweitversicherung",
    "nummerZweitversicherer" to "Nummer des Zweitversicherers",
    "behinderungsstatus" to "Behinderungsstatus",
    "sozialversicherungsnummer" to "Sozialversicherungsnummer",
    "steuerId" to "Steuer-ID",
    "steuerklasse" to "Steuerklasse",
    "kinderfreibetrag" to "Kinderfreibetrag",
    "betrieblicheAltersvorsorge" to "Betriebliche Altersvorsorge",
    "hauptbeschaeftigung" to "Hauptbeschäftigung",
    "zuschuesse" to "Zuschüsse - z.B. VWL, BAV",
    "firmenfahrzeug" to "Firmenfahrzeug",
    "kennzeichen" to "Kennzeichen",
    "insolvenzschutzNachweis" to "Insolvenzschutz-Nachweis"
)

private val bankLabels = mapOf(
    "kreditinstitut" to "Bankname",
    "kontoinhaber" to "Kontoinhaber",
    "iban" to "IBAN",
    "bic" to "BIC"
)

internal object PersonalverwaltungStyles : StyleSheet() {
    val page by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        boxSizing("border-box")
        padding(18.px, 22.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Stretch)
        color(Color.white)
        overflow("hidden")
    }
    val shell by style {
        width(100.percent)
        maxWidth(1320.px)
        minHeight(0.px)
        property("margin", "0 auto")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
    }
    val topbar by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        alignItems(AlignItems.Center)
        flexWrap(FlexWrap.Nowrap)
        gap(12.px)
        padding(18.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
    }
    val employeeHeader by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexStart)
        gap(10.px)
        minWidth(0.px)
        flexShrink(0)
    }
    val qrButtonIcon by style {
        width(16.px)
        height(16.px)
        property("filter", "brightness(0) invert(1)")
    }
    val qrDialogContent by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        gap(12.px)
    }
    val qrDialogImage by style {
        width(300.px)
        maxWidth(100.percent)
        property("aspect-ratio", "1")
        borderRadius(8.px)
        backgroundColor(Color.white)
        padding(12.px)
        boxSizing("border-box")
    }
    val qrDialogPlaceholder by style {
        width(300.px)
        maxWidth(100.percent)
        property("aspect-ratio", "1")
        borderRadius(8.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        backgroundColor(rgba(255, 255, 255, 0.06))
        color(rgba(255, 255, 255, 0.54))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        fontSize(30.px)
        fontWeight("900")
        boxSizing("border-box")
    }
    val qrDialogLabel by style {
        width(100.percent)
        color(Color.white)
        fontSize(16.px)
        fontWeight("900")
        textAlign("center")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
    }
    val employeeText by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.FlexStart)
        gap(5.px)
        minWidth(0.px)
    }
    val title by style {
        margin(0.px)
        fontSize(20.px)
        fontWeight("900")
    }
    val actions by style {
        display(DisplayStyle.Flex)
        gap(6.px)
        flexWrap(FlexWrap.Wrap)
    }
    val userSelect by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexEnd)
        gap(20.px)
        flexWrap(FlexWrap.Nowrap)
        property("flex", "1 1 auto")
        minWidth(0.px)
    }
    val employeeDropdown by style {
        width(190.px)
        maxWidth(100.percent)
        property("flex", "0 1 190px")
    }
    val historyDropdown by style {
        width(160.px)
        maxWidth(100.percent)
        property("flex", "0 1 160px")
    }
    val content by style {
        minHeight(0.px)
        overflowY("auto")
        paddingRight(4.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(22.px)
    }
    val sectionTitle by style {
        marginTop(0.px)
        marginBottom(8.px)
        color(rgba(255, 255, 255, 0.88))
        fontSize(17.px)
        fontWeight("900")
        textAlign("center")
    }
    val sectionBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
        padding(22.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.13))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
        boxSizing("border-box")
    }
    val groupTitle by style {
        color(rgba(255, 255, 255, 0.78))
        fontSize(13.px)
        fontWeight("800")
    }
    val akteSurface by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
    }
    val groupBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(14.px)
        minWidth(0.px)
        minHeight(0.px)
        paddingBottom(24.px)
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        self + ":last-child" style {
            paddingBottom(0.px)
            property("border-bottom", "0")
        }
    }
    val fieldGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        gap(14.px)
        media(mediaMaxWidth(980.px)) {
            self style {
                property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
            }
        }
        media(mediaMaxWidth(640.px)) {
            self style {
                property("grid-template-columns", "1fr")
            }
        }
    }
    val metricGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        gap(14.px)
        media(mediaMaxWidth(980.px)) {
            self style {
                property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
            }
        }
        media(mediaMaxWidth(640.px)) {
            self style {
                property("grid-template-columns", "1fr")
            }
        }
    }
    val metricValue by style {
        height(34.px)
        width(100.percent)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        borderRadius(5.px)
        backgroundColor(rgba(255, 255, 255, 0.06))
        color(Color.white)
        padding(0.px, 10.px)
        fontSize(12.px)
        fontWeight("900")
        property("white-space", "nowrap")
        overflow("hidden")
        property("text-overflow", "ellipsis")
    }
    val fieldRow by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(5.px)
    }
    val contactBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
    }
    val contactRow by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        gap(12.px)
        alignItems(AlignItems.End)
        media(mediaMaxWidth(760.px)) {
            self style {
                property("grid-template-columns", "1fr")
            }
        }
    }
    val checkboxField by style {
        minHeight(34.px)
        width(100.percent)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        padding(0.px, 10.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        borderRadius(5.px)
        backgroundColor(rgba(255, 255, 255, 0.06))
        boxSizing("border-box")
    }
    val dateField by style {
        position(Position.Relative)
        minWidth(0.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
    }
    val dateCalendarPopup by style {
        position(Position.Absolute)
        left(0.px)
        top(100.percent)
        marginTop(6.px)
        padding(10.px)
        property("z-index", "30")
        property("background", "rgba(23,25,29,0.96)")
        property("border", "1px solid rgba(255,255,255,0.10)")
        property("border-radius", "16px")
        property("box-shadow", "0 12px 28px rgba(0,0,0,0.38)")
    }
    val dateIcon by style {
        position(Position.Absolute)
        right(10.px)
        width(16.px)
        height(16.px)
        opacity(0.74)
        property("pointer-events", "none")
    }
    val fieldLabel by style {
        color(rgba(255, 255, 255, 0.70))
        fontSize(12.px)
        fontWeight("700")
    }
    val input by style {
        height(34.px)
        width(100.percent)
        boxSizing("border-box")
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        borderRadius(5.px)
        backgroundColor(rgba(255, 255, 255, 0.06))
        color(Color.white)
        padding(0.px, 10.px)
        fontSize(12.px)
        property("outline", "none")
    }
    val tableBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        gridColumn("1 / -1")
    }
    val tableTitleRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(14.px)
        paddingTop(4.px)
    }
    val tableFrame by style {
        overflowX("auto")
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.13))
        borderRadius(6.px)
    }
    val tableHeader by style {
        minWidth(760.px)
        display(DisplayStyle.Grid)
        gap(10.px)
        alignItems(AlignItems.Center)
        minHeight(38.px)
        padding(0.px, 10.px)
        boxSizing("border-box")
        backgroundColor(rgba(255, 255, 255, 0.075))
        color(rgba(255, 255, 255, 0.64))
        fontSize(10.px)
        fontWeight("900")
    }
    val tableRow by style {
        minWidth(760.px)
        display(DisplayStyle.Grid)
        gap(10.px)
        minHeight(42.px)
        padding(0.px, 10.px)
        alignItems(AlignItems.Center)
        boxSizing("border-box")
        property("border-top", "1px solid rgba(255,255,255,0.08)")
        fontSize(12.px)
    }
    val tableInput by style {
        height(30.px)
        width(100.percent)
        minWidth(0.px)
        boxSizing("border-box")
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
        borderRadius(4.px)
        backgroundColor(rgba(255, 255, 255, 0.055))
        color(Color.white)
        padding(0.px, 8.px)
        fontSize(12.px)
        property("outline", "none")
    }
    val emptyRow by style {
        minHeight(40.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        color(rgba(255, 255, 255, 0.42))
        fontSize(12.px)
        fontStyle("italic")
        property("border-top", "1px solid rgba(255,255,255,0.08)")
    }
    val message by style {
        color(Color("#84e1a7"))
        fontSize(12.px)
        fontWeight("800")
    }
    val dialogOverlay by style {
        position(Position.Fixed)
        left(0.px)
        top(0.px)
        width(100.percent)
        height(100.percent)
        property("z-index", "90")
        backgroundColor(rgba(0, 0, 0, 0.48))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        padding(20.px)
        boxSizing("border-box")
    }
    val dialogBox by style {
        width(420.px)
        maxWidth(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(14.px)
        padding(20.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.13))
        borderRadius(8.px)
        backgroundColor(rgba(23, 25, 29, 0.98))
        property("box-shadow", "0 18px 44px rgba(0,0,0,0.45)")
        boxSizing("border-box")
    }
    val dialogTitle by style {
        margin(0.px)
        color(Color.white)
        fontSize(18.px)
        fontWeight("900")
    }
    val dialogWarning by style {
        color(UISettings.ACCENT)
        fontSize(13.px)
        fontWeight("800")
        lineHeight("1.45")
    }
    val dialogField by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(6.px)
    }
    val dialogActions by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        gap(8.px)
        marginTop(4.px)
    }
}
